import { FormControl } from '../../../../../../widgets/formControl';
import { Input } from '../../../../../../widgets/input';
import { NotificationBanner } from '../../../../../../widgets/notificationBanner';
import { TextArea } from '../../../../../../widgets/textArea';
import { PropertiesPanelSwitch } from '../../scene-properties-panel/propertiesPanelSwitchProperty';
import { Tab, TabList, TabPanel } from '../../../../../../widgets/ztabs';
import { FC } from 'react';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import { stringFormat, T } from 'src/shared/helpers';
import AttributeIcon from 'src/shared/material_icons/image/tune.svg';
import styled from 'styled-components';
import Composer3DPreviewCapture from '../../../configurator-composer/composer/components/composer3DPreviewCapture';
import ComposerUploadComponent from '../../../configurator-composer/composer/components/composerUploadComponent';
import ComposerValidationTooltip from '../../../configurator-composer/composer/components/composerValidationTooltip';
import useComposerStore from '../../../configurator-composer/composer/composerStore';
import { ComposerRevealButton } from '../composerReveal';
import { createComposerLocalCode, isComposerStoreCode } from '../../../configurator-composer/composer/helpers/composerLocalCode';
import { applyComposerOptionImage, createComposerOptionImageReference } from '../../../configurator-composer/composer/helpers/composerOptionImage';
import {
    ComposerDetailValue,
    ComposerEmptyState,
    ComposerFormStack,
    ComposerKindBadge,
    ComposerPanel,
    ComposerSection,
    ComposerSettingsBody,
    ComposerSubjectContent,
    ComposerSubjectHeader,
    ComposerSubjectMark,
    ComposerSubjectMeta,
    ComposerSubjectNameInput,
    ComposerTabCount,
    ComposerTabs,
    ComposerUnsupportedNote
} from '../composerWorkspace';
import { ComposerAttributeNode, ComposerAttributeOptionNode, ComposerAttributePreviewType } from './composerAttributesModels';
import ComposerLinksEditor from './composerLinksEditor';
import ComposerRetargetPanel from './composerRetargetPanel';
import { ComposerCameraSlot, ComposerChoice, ComposerObjectSlots, ComposerSlot } from '../composerSlots';
import { ComposerGenerateOptions } from './composerGenerateOptions';
import StoreIcon from 'src/shared/material_icons/action/store.svg';
import { ComposerAttributeNodeAdapter } from '../../../configurator-composer/composer/helpers/composerNodesAdapter';
import { ComposerValidationErrorCode, hasComposerValidationError } from '../../../configurator-composer/composer/helpers/composerAttributeValidation';
import { ComposerUsedBy } from '../composerUsedBy';

// L'ANTEPRIMA DEL LAYOUT: tre opzioni affiancate, che cambiano forma e nome come nel configuratore (prototipo, .look).
const LookPreview = styled.div`
    display: flex;
    gap: 12px;
    padding: 4px 0 2px;
`;

const LookItem = styled.span`
    display: flex;
    min-width: 0;
    flex: 1;
    flex-direction: column;
    align-items: center;
    gap: 4px;
`;

const LookBall = styled.span<{ $round: boolean }>`
    width: 36px;
    height: 36px;
    overflow: hidden;
    border-radius: ${(props) => (props.$round ? '50%' : '6px')};
    background: var(--editor-sunken, #e9ebed);
    box-shadow: inset 0 0 0 1px var(--editor-line, #d1d3d8);

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

// Il nome si spegne soltanto, non sparisce: l'anteprima non cambia altezza.
const LookName = styled.span<{ $off: boolean }>`
    max-width: 100%;
    overflow: hidden;
    color: var(--editor-muted, #55595f);
    font-size: 11px;
    text-overflow: ellipsis;
    white-space: nowrap;
    opacity: ${(props) => (props.$off ? 0.25 : 1)};
    text-decoration: ${(props) => (props.$off ? 'line-through' : 'none')};
`;

// L'INSPECTOR DELL'ATTRIBUTO: Layout, Links, Retarget e Settings. Le opzioni stanno nell'albero (round 4, pacchetto H,
// `composerOptionRows.tsx`); qui resta il comando che ne aggiunge una.
const ComposerOptions: FC<{
    selectedAttribute: ComposerAttributeNode | null;
    options: ComposerAttributeOptionNode[];
    multiple: boolean;
}> = ({ selectedAttribute, options, multiple }) => {
    const [
        attributes,
        sceneMaterialList,
        sceneMeshList,
        updateAttribute,
        updateOption,
        selectOptions,
        validateAttributes,
        validateOptions,
        attributeFace,
        setAttributeFace,
        merchantProductOptions,
        merchantProductOptionsError
    ] = useComposerStore((store) => [
        store.attributes,
        store.sceneMaterialList,
        store.sceneObjectList,
        store.updateAttribute,
        store.updateOption,
        store.selectOptions,
        store.validateAttributes,
        store.validateOptions,
        store.attributeFace,
        store.setAttributeFace,
        store.merchantProductOptions,
        store.merchantProductOptionsError
    ]);
    const [zkScene, sceneEditor] = usePreview3DStore((store) => [store.zkScene, store.sceneEditor]);

    const materialsForGenerate = sceneMaterialList.filter((material) => material.name && material.name !== 'default material');

    if (!selectedAttribute || multiple) {
        return (
            <ComposerPanel>
                <ComposerEmptyState icon={<AttributeIcon />}>{T._('Select one attribute to see its options and settings.', 'Admin')}</ComposerEmptyState>
            </ComposerPanel>
        );
    }

    // LE TAB DEL PROTOTIPO, COI NOSTRI NOMI (utente, 2026-10-20): Layout, Links, Retarget, Settings; si apre su Layout.
    const activeTab = attributeFace === 'links' ? 1 : attributeFace === 'retarget' ? 2 : attributeFace === 'settings' ? 3 : 0;
    const updateSelectedAttribute = (changes: Partial<ComposerAttributeNode>) => {
        updateAttribute({ ...selectedAttribute, ...changes, isNew: true });
        if (changes.name !== undefined || changes.code !== undefined || Object.prototype.hasOwnProperty.call(changes, 'platformAttribute')) {
            void validateAttributes();
        }
    };
    const optionsWithImage = selectedAttribute.options.filter((option) => !!option.img).length;
    const commonOptionImage = selectedAttribute.options[0]?.img;
    const hasCommonOptionImage = !!commonOptionImage && selectedAttribute.options.every((option) => option.img === commonOptionImage);
    const updateAttributeOptionImages = async (uploadedImageUrl: string, tempImageFileKey?: string) => {
        const image = createComposerOptionImageReference(uploadedImageUrl, tempImageFileKey);
        const updatedOptions = selectedAttribute.options.map((option) => applyComposerOptionImage(option, image));
        updatedOptions.forEach((option) => updateOption(option));
        await validateOptions({ ...selectedAttribute, options: updatedOptions });
        return image?.imageUrl ?? '';
    };
    const platformAttributes = [
        { value: '', label: T._('Not connected', 'Admin') },
        ...merchantProductOptions.map((attribute) => ({
            value: attribute.optionID,
            label: attribute.label || attribute.optionID
        }))
    ];
    const changePlatformAttribute = (selection: { value: string; label: string } | null) => {
        const platformAttribute = selection?.value ? { id: selection.value, label: selection.label } : undefined;
        const storeAttribute = platformAttribute ? merchantProductOptions.find((attribute) => attribute.optionID === platformAttribute.id) : undefined;
        const wasStoreAttribute = Boolean(selectedAttribute.platformAttribute?.id) || isComposerStoreCode(selectedAttribute.code);

        const updatedOptions = platformAttribute
            ? (storeAttribute?.values ?? []).map((storeOption, displayOrder) => {
                  const existingOption = selectedAttribute.options.find((option) => option.platformAttribute?.id === storeOption.valueID);
                  const option =
                      existingOption ??
                      ComposerAttributeNodeAdapter.toNodeOption(selectedAttribute.id, ComposerAttributeNodeAdapter.createNewOption(displayOrder));
                  return {
                      ...option,
                      attributeID: selectedAttribute.id,
                      name: storeOption.label || storeOption.valueID,
                      code: JSON.stringify({ id: storeOption.valueID, label: storeOption.label || storeOption.valueID, selected: true }),
                      displayOrder,
                      isDefault: existingOption?.isDefault ?? displayOrder === 0,
                      isNew: existingOption?.isNew ?? true,
                      platformAttribute: { id: storeOption.valueID, label: storeOption.label || storeOption.valueID }
                  };
              })
            : selectedAttribute.options
                  .filter((option) => !option.platformAttribute?.id && !isComposerStoreCode(option.code))
                  .map((option, displayOrder) => ({ ...option, displayOrder }));

        const defaultOption = updatedOptions.find((option) => option.isDefault) ?? updatedOptions[0];
        const normalizedOptions = updatedOptions.map((option) => ({ ...option, isDefault: option.id === defaultOption?.id }));
        const retainedOptionIds = new Set(normalizedOptions.map((option) => option.id));

        selectOptions(defaultOption ? [normalizedOptions.find((option) => option.id === defaultOption.id)!] : []);
        updateSelectedAttribute({
            platformAttribute,
            options: normalizedOptions,
            pricings: selectedAttribute.pricings.filter((pricing) => retainedOptionIds.has(pricing.selectedOptionGuid)),
            code: platformAttribute
                ? JSON.stringify({ ...platformAttribute, selected: true })
                : wasStoreAttribute
                  ? createComposerLocalCode(selectedAttribute.name, 'attribute')
                  : selectedAttribute.code
        });
    };
    const cameraOptions =
        zkScene
            ?.get?.('cameraLocations')
            ?.models?.map((location) => ({ value: location.getLocationID(), label: location.get('name') || location.getLocationID() })) ?? [];
    const meshOptions = sceneMeshList.filter((mesh) => sceneEditor?.isRealMesh(mesh)).map((mesh) => ({ value: mesh.id, label: mesh.name || mesh.id }));
    const selectedMeshes = (selectedAttribute.objects || '').split(/[;,]/).filter(Boolean);
    const attributeNameHasError = hasComposerValidationError(
        selectedAttribute,
        ComposerValidationErrorCode.ATTRIBUTE_NAME_REQUIRED,
        ComposerValidationErrorCode.ATTRIBUTE_NAME_NOT_UNIQUE
    );
    const attributeCodeHasError = hasComposerValidationError(
        selectedAttribute,
        ComposerValidationErrorCode.ATTRIBUTE_CODE_REQUIRED,
        ComposerValidationErrorCode.ATTRIBUTE_CODE_NOT_UNIQUE,
        ComposerValidationErrorCode.STORE_CODE_INVALID
    );
    const attributeStoreHasError = hasComposerValidationError(
        selectedAttribute,
        ComposerValidationErrorCode.BIGCOMMERCE_ATTRIBUTE_NOT_CONNECTED,
        ComposerValidationErrorCode.STORE_ATTRIBUTE_NOT_FOUND,
        ComposerValidationErrorCode.STORE_ATTRIBUTE_NOT_UNIQUE,
        ComposerValidationErrorCode.STORE_CODE_INVALID
    );

    return (
        <ComposerPanel aria-label={T._('Attribute options', 'Admin')}>
            <ComposerSubjectHeader>
                <ComposerSubjectMark $kind='attribute'>
                    <AttributeIcon />
                </ComposerSubjectMark>
                <ComposerSubjectContent>
                    <ComposerSubjectNameInput
                        aria-label={T._('Attribute name', 'Admin')}
                        placeholder={T._('Enter attribute name', 'Admin')}
                        isError={attributeNameHasError}
                        value={selectedAttribute.name}
                        onChange={(event) => updateSelectedAttribute({ name: event.target.value })}
                        onBlur={validateAttributes}
                    />
                    <ComposerSubjectMeta>
                        <ComposerKindBadge $kind='attribute'>{T._('Attribute', 'Admin')}</ComposerKindBadge>
                        <span>{stringFormat(options.length === 1 ? T._('{0} option', 'Admin') : T._('{0} options', 'Admin'), options.length.toString())}</span>
                        <ComposerUsedBy subjectID={selectedAttribute.id} kind='attribute' />
                    </ComposerSubjectMeta>
                </ComposerSubjectContent>
                <ComposerRevealButton id={selectedAttribute.id} />
            </ComposerSubjectHeader>
            {/* GENERATE OPTIONS SOTTO LA TESTATA (prototipo, utente 2026-10-20): prende il posto dei suggerimenti Colors / Sizes /
                Materials dell'attributo vuoto, e resta anche dopo, per aggiungerne altre. */}
            <ComposerGenerateOptions
                attribute={selectedAttribute}
                meshes={sceneMeshList.filter((mesh) => sceneEditor?.isRealMesh(mesh))}
                materials={materialsForGenerate}
            />
            <ComposerTabs
                selectedIndex={activeTab}
                onSelect={(index) => setAttributeFace(index === 1 ? 'links' : index === 2 ? 'retarget' : index === 3 ? 'settings' : 'layout')}
            >
                <TabList hasOverflow>
                    <Tab>{T._('Layout', 'Admin')}</Tab>
                    <Tab>
                        {T._('Links', 'Admin')} <ComposerTabCount>{selectedAttribute.links.length}</ComposerTabCount>
                    </Tab>
                    <Tab>{T._('Retarget', 'Admin')}</Tab>
                    <Tab>{T._('Settings', 'Admin')}</Tab>
                </TabList>
                <TabPanel>
                    <ComposerSettingsBody>
                        <ComposerFormStack>
                            {/* LAYOUT: COME IL CLIENTE VEDE LE OPZIONI (prototipo, Choice appearance; utente 2026-10-20). Le prime tre
                                opzioni, con la forma e il nome come li mostrerà il configuratore. */}
                            <ComposerSection id='attribute.layout' title={T._('Option appearance', 'Admin')}>
                                <LookPreview aria-hidden='true'>
                                    {(options.length ? options.slice(0, 3) : [null]).map((option, index) => (
                                        <LookItem key={option?.id ?? index}>
                                            <LookBall $round={selectedAttribute.previewType === ComposerAttributePreviewType.Circle}>
                                                {option?.img && <img src={option.img} alt='' />}
                                            </LookBall>
                                            <LookName $off={!!selectedAttribute.hideOptionsLabel}>{option?.name ?? T._('Option', 'Admin')}</LookName>
                                        </LookItem>
                                    ))}
                                </LookPreview>
                                <FormControl label={T._('Shape', 'Admin')}>
                                    <ComposerChoice
                                        ariaLabel={T._('Shape', 'Admin')}
                                        options={[
                                            { value: ComposerAttributePreviewType.Rectangle, label: T._('Square', 'Admin') },
                                            { value: ComposerAttributePreviewType.Circle, label: T._('Circle', 'Admin') }
                                        ]}
                                        value={
                                            selectedAttribute.previewType === ComposerAttributePreviewType.Circle
                                                ? ComposerAttributePreviewType.Circle
                                                : ComposerAttributePreviewType.Rectangle
                                        }
                                        onChange={(previewType) => updateSelectedAttribute({ previewType })}
                                    />
                                </FormControl>
                                <FormControl label={T._('Show the name', 'Admin')}>
                                    <PropertiesPanelSwitch
                                        ariaLabel={T._('Show the name', 'Admin')}
                                        checked={!selectedAttribute.hideOptionsLabel}
                                        onChange={(checked) => updateSelectedAttribute({ hideOptionsLabel: !checked })}
                                    />
                                </FormControl>
                            </ComposerSection>
                        </ComposerFormStack>
                    </ComposerSettingsBody>
                </TabPanel>
                <TabPanel>
                    <ComposerLinksEditor
                        attributes={attributes}
                        links={selectedAttribute.links}
                        logicalOperator={selectedAttribute.linkOperation}
                        selfAttributeID={selectedAttribute.id}
                        subjectLabel={T._('This attribute', 'Admin')}
                        validationErrors={selectedAttribute.brokenValues}
                        onChange={(links, linkOperation) => {
                            updateSelectedAttribute({ links, linkOperation });
                            validateAttributes();
                        }}
                    />
                </TabPanel>
                <TabPanel>
                    <ComposerRetargetPanel
                        actions={selectedAttribute.options.flatMap((option) => option.actions)}
                        objects={sceneMeshList}
                        materials={sceneMaterialList}
                        subjectLabel={selectedAttribute.name}
                        onApply={(remapped) =>
                            selectedAttribute.options.forEach((option) =>
                                updateOption({
                                    ...option,
                                    actions: option.actions.map((action) => remapped.find((candidate) => candidate.id === action.id) ?? action)
                                })
                            )
                        }
                    />
                </TabPanel>
                <TabPanel>
                    <ComposerSettingsBody>
                        <ComposerFormStack>
                            <ComposerSection id='attribute.general' forceOpen={attributeCodeHasError || attributeStoreHasError} title={T._('General', 'Admin')}>
                                <FormControl label={T._('Code', 'Admin')}>
                                    {selectedAttribute.platformAttribute?.id ? (
                                        <ComposerDetailValue>
                                            {T._('Managed by the connected store attribute. Disconnect it to edit a local code.', 'Admin')}
                                        </ComposerDetailValue>
                                    ) : (
                                        <Input
                                            placeholder={T._('Enter attribute code', 'Admin')}
                                            isError={attributeCodeHasError}
                                            value={selectedAttribute.code}
                                            onChange={(event) => updateSelectedAttribute({ code: event.target.value })}
                                        />
                                    )}
                                </FormControl>
                                <FormControl
                                    label={
                                        <>
                                            {T._('Store attribute (optional)', 'Admin')}
                                            {merchantProductOptionsError && <ComposerValidationTooltip messages={[merchantProductOptionsError]} />}
                                        </>
                                    }
                                >
                                    <ComposerSlot
                                        id={`composer-attribute-${selectedAttribute.id}-store`}
                                        value={selectedAttribute.platformAttribute?.id || undefined}
                                        options={platformAttributes.filter((attribute) => attribute.value)}
                                        glyph={<StoreIcon />}
                                        caption={T._('Store attribute', 'Admin')}
                                        emptyLabel={T._('Choose a store attribute', 'Admin')}
                                        searchPlaceholder={T._('Search store attributes…', 'Admin')}
                                        noOptionsLabel={T._('No store attributes are available for this product. Create them in your store first.', 'Admin')}
                                        missingLabel={selectedAttribute.platformAttribute?.label}
                                        isError={attributeStoreHasError}
                                        disabled={Boolean(merchantProductOptionsError)}
                                        clearable
                                        onChange={(value) =>
                                            changePlatformAttribute(platformAttributes.find((attribute) => attribute.value === value && value) ?? null)
                                        }
                                    />
                                </FormControl>
                                <FormControl label={T._('Description', 'Admin')}>
                                    <TextArea
                                        rows={3}
                                        placeholder={T._('Enter attribute description', 'Admin')}
                                        value={selectedAttribute.description}
                                        onChange={(event) => updateSelectedAttribute({ description: event.target.value })}
                                    />
                                </FormControl>
                            </ComposerSection>
                            {/* SONO LE IMMAGINI DELLE OPZIONI, NON DELL'ATTRIBUTO (utente, 2026-10-16). Un attributo un'immagine sua
                                non ce l'ha — né campo né salvataggio, `MPlaza.Attribute` —, e questa sezione ne mette una uguale su
                                tutte le sue opzioni. Col titolo «Preview images» sembrava l'immagine dell'attributo, e scoprire che
                                aveva sostituito quelle delle opzioni era una sorpresa. */}
                            <ComposerSection id='attribute.preview-images' title={T._('Option images', 'Admin')}>
                                {selectedAttribute.options.length > 0 && (
                                    <FormControl
                                        label={T._('Upload one image for all options', 'Admin')}
                                        caption={T._('The uploaded image will replace the preview image of every option in this attribute.', 'Admin')}
                                    >
                                        <ComposerUploadComponent
                                            passedImgUrl={hasCommonOptionImage ? commonOptionImage : undefined}
                                            onImagePick={updateAttributeOptionImages}
                                        />
                                    </FormControl>
                                )}
                                {optionsWithImage > 0 && (
                                    <NotificationBanner
                                        type='WARNING'
                                        variant='slim'
                                        content={
                                            optionsWithImage === 1
                                                ? T._('1 option already has a preview image. It will be replaced by the new one.', 'Admin')
                                                : T._(
                                                      `${optionsWithImage} options already have a preview image. They will be replaced by the new one.`,
                                                      'Admin'
                                                  )
                                        }
                                    />
                                )}
                                <Composer3DPreviewCapture
                                    materials={sceneMaterialList}
                                    disabled={selectedAttribute.options.length === 0}
                                    helperText={T._(
                                        'Create one image from the 3D scene and apply it to all the options of this attribute. Use object captures the object as it is shown now, Use material captures the selected material.',
                                        'Admin'
                                    )}
                                    onImagePick={updateAttributeOptionImages}
                                    successMessage={T._('The 3D preview was assigned to all options of this attribute.', 'Admin')}
                                />
                                {selectedAttribute.options.length === 0 && (
                                    <ComposerUnsupportedNote>
                                        {T._('Add at least one option before assigning its preview image.', 'Admin')}
                                    </ComposerUnsupportedNote>
                                )}
                            </ComposerSection>
                            {/* EVENTS IN DUE SECTION (prototipo, utente 2026-10-20): dove va la camera, e quali oggetti la mandano lì. */}
                            <ComposerSection id='attribute.camera' title={T._('Camera position', 'Admin')}>
                                <FormControl
                                    label={T._('Camera position', 'Admin')}
                                    tooltipLabelHtml={T._(
                                        'The camera moves to this position when the customer selects this attribute in the configurator.',
                                        'Admin'
                                    )}
                                >
                                    <ComposerCameraSlot
                                        id={`composer-attribute-${selectedAttribute.id}-camera`}
                                        value={selectedAttribute.camera}
                                        options={cameraOptions}
                                        onChange={(camera) => updateSelectedAttribute({ camera })}
                                    />
                                </FormControl>
                            </ComposerSection>
                            <ComposerSection id='attribute.trigger' title={T._('Object trigger', 'Admin')}>
                                <FormControl
                                    label={T._('Objects', 'Admin')}
                                    tooltipLabelHtml={T._('When you click on these objects the camera will move to the selected position', 'Admin')}
                                >
                                    <ComposerObjectSlots
                                        id={`composer-attribute-${selectedAttribute.id}-objects`}
                                        value={selectedMeshes}
                                        options={meshOptions}
                                        onChange={(ids) => updateSelectedAttribute({ objects: ids.join(';') })}
                                    />
                                </FormControl>
                            </ComposerSection>
                        </ComposerFormStack>
                    </ComposerSettingsBody>
                </TabPanel>
            </ComposerTabs>
        </ComposerPanel>
    );
};

export default ComposerOptions;
